// entry-story.js -- how an entry about content reads, and how a story of
// entries reads as one (owner 2026-10-09: one publish read as four rows of
// raw types, with no hint of the course).
//
// Content places every entry about content (entry.context: the course and
// its folder, an asset's DataType, a version's number, size and whether a
// checksum was recorded) and keys its story (entry.story: a build's
// publish across its init and complete requests; one request about one
// course). This module turns a story's entries -- one entry is a story of
// one -- into:
//   headline: segments for a sentence after the person's name ("published
//     v14 of the Web build for [Test Assistant] in [Promotional]"); null
//     when the entry is not about content (the server's sentence stands);
//   chips: what the publish came to (current v13 → v14, upload complete ·
//     207 MB), and "no file fingerprint" for a finished build that saved
//     none;
//   lines: each entry in plain words, oldest first ("Web build v13: no
//     longer current"; "..., file fingerprint saved (SHA-256)" where one
//     was saved).
// Segments: a string, { strong }, { version }, or { place, role } (a course
// or folder chip). A file's name ({ strong }) and its version carry `file`:
// what their links need (owner 2026-10-09: "v4 ... the version details page
// and macOS linked to the mac os asset"), and `said` when the headline's
// own verb already says that record is gone ("deleted v13 ...").
//
// The checksum (owner 2026-10-09: "checksum recorded feels too long ...
// less technical") is a file fingerprint here. Every finished upload from
// the editor saves one, so on the row it said nothing; it is never called
// verified, because nothing checks it against the file.
import { dataTypeView, sizeText } from './data-types'
import { dayLabel, recordView } from './entry-format'
import { formatCount } from '../sso-workspace/format'

// The stories of a timeline (newest first): entries with one story key
// together, where the newest of them is; anything without a key alone.
export const groupStories = items => {
  const out = []
  const byKey = new Map()
  ;(items || []).forEach(item => {
    const key = item && item.story
    if (key && byKey.has(key)) {
      byKey.get(key).items.push(item)
      return
    }
    const group = { key: key || `entry:${item && item.id}`, items: [item] }
    if (key) byKey.set(key, group)
    out.push(group)
  })
  return out
}

// A headline as plain text (tests, titles).
export const headlineText = segments =>
  (segments || [])
    .map(s => (typeof s === 'string' ? s : s.strong || (s.version !== undefined ? `v${s.version}` : s.place ? s.place.name : '')))
    .join('')

// What a node is called in a sentence.
const NOUNS = {
  COURSE: 'course',
  STREAMING_VIDEO: 'video',
  CATEGORY: 'folder',
  COURSE_TEMPLATE: 'template',
  COURSE_TEMPLATE_CATEGORY: 'folder',
  NODE_LIBRARY: 'library',
  NODE_LIBRARY_CATEGORY: 'folder'
}
export const nodeNoun = type => NOUNS[String(type || '').toUpperCase()] || (/^BUNDLE/i.test(type || '') ? 'bundle' : 'item')

// The facts of one entry, from its own record's field rows (a row about
// another record carries c.record) and its action.
const truthy = v => v === true || v === 'true'
const factsOf = entry => {
  const own = ((entry && entry.changes) || []).filter(c => !c.record)
  const all = (entry && entry.changes) || []
  const field = name => own.find(c => c.field === name)
  const to = name => {
    const c = field(name)
    return c ? c.to : undefined
  }
  const action = (entry && entry.action) || ''
  const created = action === 'asset.version.uploaded' || action === 'change.created' || own.some(c => c.op === 'INSERT')
  const removed = action === 'change.deleted' || own.some(c => c.op === 'DELETE')
  const recycled = action === 'asset.version.deleted' || action === 'change.recycled'
  const restored = action === 'asset.version.restored' || action === 'change.restored'
  const status = to('status')
  const cur = field('is_current')
  const madeCurrent = action === 'asset.version.made_current'
  return {
    own,
    all,
    field,
    created,
    removed,
    recycled,
    restored,
    status,
    uploaded: status === 'complete' || (field('is_uploaded') && truthy(to('is_uploaded')) && field('is_uploaded').op === 'UPDATE'),
    failed: status === 'error',
    canceled: status === 'canceled',
    current: Boolean(cur && truthy(cur.to)),
    notCurrent: Boolean(cur && cur.op === 'UPDATE' && truthy(cur.from) && !truthy(cur.to)),
    madeCurrent
  }
}

// An asset's or a version's status (current, recycled, moved or deleted):
// the one an entry's context names for it (content's read, which sees the
// recycle bin for every reader), else the ledger's names for it in these
// entries (an entry's record, its audit parent, a field row's record --
// which name every live row current for a super admin, and an older
// content's context names none); current when nothing names one.
const CONTEXT_STATUS = { assets: ['assetID', 'assetStatus'], asset_versions: ['versionID', 'versionStatus'] }
const statusIn = (entries, type, id) => {
  if (!id) return null
  const [idKey, statusKey] = CONTEXT_STATUS[type]
  const placed = entries.find(e => e.context && e.context[statusKey] && String(e.context[idKey]) === String(id))
  if (placed) return placed.context[statusKey]
  const named = entries
    .flatMap(e => [e.record, e.parent, ...((e.changes || []).map(c => c.record))])
    .find(r => r && r.type === type && String(r.id) === String(id) && r.status)
  return named ? named.status : 'current'
}

// What a headline's file name and version link to: the node whose page
// holds the file (the course), the file's kind in words, the asset and the
// version, each with its status.
const fileOf = (entries, ctx, dt, versionID = null) => ({
  place: (ctx && ctx.course) || null,
  name: dt.name,
  assetID: (ctx && ctx.assetID) || null,
  asset: statusIn(entries, 'assets', ctx && ctx.assetID),
  versionID: versionID || null,
  version: statusIn(entries, 'asset_versions', versionID)
})

// A non-empty value: a fingerprint saved, not one cleared.
const filled = v => v !== null && v !== undefined && String(v).trim() !== ''
const savesFingerprint = f => f.own.some(c => c.field === 'content_sha256' && filled(c.to))
const FINGERPRINT = 'file fingerprint saved (SHA-256)'

const isVersion = e => e && e.record && e.record.type === 'asset_versions'
const isAsset = e => e && e.record && e.record.type === 'assets'
const isNode = e => e && e.record && e.record.type === 'nodes' && e.context && e.context.course && String(e.record.id) === String(e.context.course.id)
const versionOf = e => {
  const c = e.context || {}
  const n = c.version || (e.record && (e.record.versionCount ?? e.record.version_count))
  if (n) return Number(n)
  const f = factsOf(e).field('version_count')
  return f && f.to ? Number(f.to) : null
}

// The build these entries published: the context of a version created or
// finished in them (a publish, whether or not it finished), else null. Its
// caffeineVersion and unityVersion (what the editor wrote into the build at
// publish) name the editor that published it -- the editor's app chip
// falls back to them when the request's own header names no caffeine
// version (owner 2026-10-09). A version only made current, deleted or
// restored, or an asset's own entry, is no publish: its build was made by
// whoever published it.
export const publishedBuild = entries => {
  const v = (entries || []).filter(isVersion).find(e => {
    const f = factsOf(e)
    return f.created || f.uploaded || f.failed || f.canceled
  })
  return (v && v.context) || null
}

// The course and folder segments: "[course]" and " in [folder]" when it has
// one.
const where = (ctx, { folder = true } = {}) => {
  const out = []
  if (ctx && ctx.course) out.push({ place: ctx.course, role: 'course' })
  if (folder && ctx && ctx.folder) out.push(' in ', { place: ctx.folder, role: 'folder' })
  return out
}

const inFolder = ctx => (ctx && ctx.folder ? [' in ', { place: ctx.folder, role: 'folder' }] : [])

// The story's context: the first entry that has one.
const contextOf = entries => (entries.find(e => e.context && e.context.course) || entries.find(e => e.context) || {}).context || null

// A version story: the version it is about and what happened to it.
const versionStory = (entries, ctx) => {
  const versions = entries.filter(isVersion)
  const made = entries.find(e => factsOf(e).madeCurrent)
  const facts = versions.map(e => ({ e, f: factsOf(e), n: versionOf(e) }))
  // the version the story is about: one created or finished, else one made
  // current, else the newest
  const main =
    facts.find(x => x.f.uploaded || x.f.failed || x.f.canceled) ||
    facts.find(x => x.f.created) ||
    facts.find(x => x.f.current) ||
    facts[0] ||
    null
  const lost = facts.find(x => x !== main && x.f.notCurrent)
  const any = pred => facts.some(x => x.n === (main && main.n) && pred(x.f))
  const n = main ? main.n : made && made.data ? Number(made.data.to) : null
  // the version's id: the main version's own, else the one Set active made
  // current (its field row names it)
  const madeRow = made && !main ? (made.changes || []).find(c => c.record && c.record.type === 'asset_versions' && c.field === 'is_current' && truthy(c.to)) : null
  const versionID = main ? (main.e.context && main.e.context.versionID) || main.e.record.id : madeRow ? madeRow.record.id : null
  return {
    n,
    versionID,
    main,
    lost,
    made,
    created: any(f => f.created),
    uploaded: any(f => f.uploaded) || any(f => f.created && f.status === 'complete'),
    failed: any(f => f.failed),
    canceled: any(f => f.canceled),
    current: any(f => f.current) || Boolean(made),
    deleted: any(f => f.recycled || f.removed),
    restored: any(f => f.restored),
    ctx: (main && main.e.context) || ctx
  }
}

const versionHeadline = (s, dt, ctx, file) => {
  // a deletion's verb says its version is gone
  const said = s.deleted ? { said: true } : {}
  const v = s.n ? [{ version: s.n, file, ...said }] : []
  const name = { strong: dt.phrase, file }
  const of = (verb, tail = []) => [`${verb} `, ...(v.length ? [...v, ' of '] : []), 'the ', name, ...tail]
  const forCourse = [' for ', ...where(ctx)]
  if (s.deleted) return of('deleted', forCourse)
  if (s.restored) return of('restored', forCourse)
  if (s.failed) return of("couldn't finish uploading", forCourse)
  if (s.canceled) return of('canceled the upload of', forCourse)
  if (s.uploaded || (s.created && s.current)) {
    switch (dt.kind) {
      // the version follows the course it is of: "of [Test Assistant] v73"
      case 'scene':
        return ['saved the ', name, ' of ', ...where(ctx, { folder: false }), ...(v.length ? [' ', ...v] : []), ...inFolder(ctx)]
      case 'snapshot':
        return ['saved ', name, ' in ', ...where(ctx, { folder: false }), ...(v.length ? [' ', ...v] : []), ...inFolder(ctx)]
      case 'thumbnail':
      case 'picture':
        return ['changed the ', name, ' of ', ...where(ctx)]
      case 'build':
      case 'code':
      case 'overview':
        return of('published', forCourse)
      default:
        return of('uploaded', forCourse)
    }
  }
  if (s.created) return of('started uploading', forCourse)
  if (s.current) return ['made ', ...v, ' the current ', name, ...forCourse]
  return of('changed', forCourse)
}

const assetHeadline = (e, dt, ctx, file) => {
  const f = factsOf(e)
  const name = { strong: dt.phrase, file }
  const thing = dt.kind === 'thumbnail' ? ['a ', name] : ['the ', name]
  if (f.created) return ['added ', ...thing, ' to ', ...where(ctx)]
  // the verb says the file is gone
  if (f.recycled || f.removed) return ['removed the ', { ...name, said: true }, ' from ', ...where(ctx)]
  if (f.restored) return ['restored the ', name, ' of ', ...where(ctx)]
  return ['changed the ', name, ' of ', ...where(ctx)]
}

const nodeHeadline = (e, ctx) => {
  const f = factsOf(e)
  const noun = nodeNoun(ctx.course.type)
  const it = { place: ctx.course, role: 'course' }
  const folder = ctx.folder ? { place: ctx.folder, role: 'folder' } : null
  if (f.created) return [`added the ${noun} `, it, ...(folder ? [' to ', folder] : [])]
  if (f.removed) return [`permanently deleted the ${noun} `, it]
  if (f.recycled) return [`deleted the ${noun} `, it, ...(folder ? [' from ', folder] : [])]
  if (f.restored) return [`restored the ${noun} `, it]
  if (f.field('parent_id')) return [`moved the ${noun} `, it, ...(folder ? [' to ', folder] : [' to the top level'])]
  if (f.field('name')) return [`renamed the ${noun} `, it]
  return [`changed the ${noun} `, it, ...(folder ? [' in ', folder] : [])]
}

// One entry of a story in plain words.
const versionLine = (e, dt) => {
  const f = factsOf(e)
  const name = dt ? dt.name : 'Version'
  const n = versionOf(e)
  const label = n ? `${name} v${n}` : name
  const fingerprint = savesFingerprint(f)
  if (f.created) {
    const state = f.status === 'pending' ? ' (pending)' : f.status === 'complete' ? '' : f.status ? ` (${String(f.status)})` : ''
    return `Added ${label}${state}${fingerprint ? `, ${FINGERPRINT}` : ''}`
  }
  if (f.removed) return `${label}: deleted`
  const parts = []
  if (f.uploaded) parts.push('upload finished')
  if (f.failed) parts.push('upload failed')
  if (f.canceled) parts.push('upload canceled')
  if (f.current) parts.push('now current')
  if (f.notCurrent) parts.push('no longer current')
  if (f.recycled || f.removed) parts.push('deleted')
  if (f.restored) parts.push('restored')
  if (fingerprint) parts.push(FINGERPRINT)
  const said = new Set(['status', 'is_uploaded', 'is_current', 'deleted_at', 'version_count', 'upload_identifier', 'content_length', 'content_sha256'])
  const rest = f.own.filter(c => c.op === 'UPDATE' && !said.has(c.field)).map(c => `${String(c.label || c.field).toLowerCase()} changed`)
  return `${label}: ${[...parts, ...rest].join(', ') || 'changed'}`
}

const assetLine = (e, dt) => {
  const f = factsOf(e)
  const name = dt ? dt.name : 'Asset'
  if (f.created) return dt && dt.kind === 'thumbnail' ? `Added a ${dt.phrase}` : `Added the ${dt ? dt.phrase : 'asset'}`
  if (f.recycled || f.removed) return `Removed the ${dt ? dt.phrase : 'asset'}`
  if (f.restored) return `Restored the ${dt ? dt.phrase : 'asset'}`
  const count = f.field('version_count')
  const parts = []
  if (count) parts.push(count.from ? `versions ${count.from} → ${count.to}` : `${count.to} versions`)
  f.own.filter(c => c.field !== 'version_count').forEach(c => parts.push(`${String(c.label || c.field).toLowerCase()} changed`))
  return `${name}: ${parts.join(', ') || 'changed'}`
}

const nodeLine = e => {
  const f = factsOf(e)
  const noun = nodeNoun(e.context.course.type)
  const name = e.context.course.name
  if (f.created) return `Added the ${noun} ${name}`
  if (f.removed) return `Permanently deleted the ${noun} ${name}`
  if (f.recycled) return `Deleted the ${noun} ${name}`
  if (f.restored) return `Restored the ${noun} ${name}`
  if (f.field('parent_id')) return `Moved the ${noun} ${name}`
  if (f.field('name')) return `Renamed the ${noun} ${name}`
  return `Changed the ${noun} ${name}`
}

// Views (owner 2026-10-09, prod: "it says Cory Heizenrader changed the
// course but he didn't change anything"). A view is one person's launches of
// a course, or openings of a record's page, on one day (audit.views: one row
// per person, kind, record and day, with its count) -- never a change. It
// reads by its kind: "launched the course", "opened the folder", "viewed the
// <record>", its count in words ("2 times today"), never a "×2" chip.
const VIEW_ACTIONS = new Set(['course.launched', 'record.viewed'])
export const isView = e => Boolean(e) && (VIEW_ACTIONS.has(e.action) || e.category === 'views')

// A day's count in words: nothing for once; "2 times today", "… yesterday",
// "… that day" (the timeline's own local days).
export const timesText = (count, iso, now = new Date()) => {
  const n = Number(count) || 0
  if (n <= 1) return ''
  const day = dayLabel(iso, now)
  return `${formatCount(n)} times ${day === 'Today' ? 'today' : day === 'Yesterday' ? 'yesterday' : 'that day'}`
}

// What a view did, and to what: placed when its record is a course or
// folder itself (its chip); otherwise its record's label ("assessment").
export const viewWords = (e, now = new Date()) => {
  const placed = isNode(e)
  const nodes = e && e.record && e.record.type === 'nodes'
  const noun = placed
    ? nodeNoun(e.context.course.type)
    : nodes && e.action === 'course.launched' ? 'course' : String((e && e.record && e.record.label) || 'record').toLowerCase()
  const verb = e && e.action === 'course.launched' ? 'launched' : noun === 'folder' ? 'opened' : 'viewed'
  return { verb, noun, placed, times: timesText(e && e.count, e && e.occurredAt, now) }
}

// A view of a course or folder: "launched the course [course] in [folder] 2
// times today".
const viewHeadline = (e, now) => {
  const w = viewWords(e, now)
  return [`${w.verb} the ${w.noun} `, { place: e.context.course, role: 'course' }, ...inFolder(e.context), ...(w.times ? [` ${w.times}`] : [])]
}

// Any view as text (the Audit log table, a story's line): its record, and
// the course it is in.
export const viewText = (e, now = new Date()) => {
  const w = viewWords(e, now)
  const ctx = (e && e.context) || {}
  const name = w.placed ? ctx.course.name : recordView(e.record).text
  const where = w.placed ? (ctx.folder ? ctx.folder.name : '') : ctx.course ? ctx.course.name : ''
  return [`${w.verb} the ${w.noun}`, name, where && `in ${where}`, w.times].filter(Boolean).join(' ')
}

const lineOf = (e, now) => {
  if (isView(e)) {
    const t = viewText(e, now)
    return t[0].toUpperCase() + t.slice(1)
  }
  const dt = e.context ? dataTypeView(e.context.dataType) : null
  if (factsOf(e).madeCurrent && e.data) {
    return `${dt ? dt.name : 'Version'}: v${e.data.to} now current${e.data.from ? ` (was v${e.data.from})` : ''}`
  }
  if (isVersion(e)) return versionLine(e, dt)
  if (isAsset(e)) return assetLine(e, dt)
  if (isNode(e)) return nodeLine(e)
  const s = String(e.sentence || 'changed')
  return s[0].toUpperCase() + s.slice(1)
}

// A story's entries for its lines: oldest request first; within one
// request, the way a person tells it -- the course, the new version, the
// asset's count, the version finished or made current, the one it
// replaced.
const rankOf = e => {
  if (isNode(e)) return 0
  const f = factsOf(e)
  if (isVersion(e) && f.created) return 1
  if (isAsset(e)) return 2
  if (isVersion(e) && f.notCurrent) return 4
  return 3
}
const inOrder = list => {
  const oldest = [...list].reverse()
  const req = new Map()
  oldest.forEach(e => {
    const k = e.requestID || e.id
    if (!req.has(k)) req.set(k, req.size)
  })
  return oldest
    .map((e, i) => ({ e, i, r: req.get(e.requestID || e.id), k: rankOf(e) }))
    .sort((a, b) => a.r - b.r || a.k - b.k || a.i - b.i)
    .map(x => x.e)
}

// The kinds the editor publishes, each with a file fingerprint saved by
// every finished upload; a thumbnail, a picture or saved progress never has
// one, so its absence says nothing there.
const FINGERPRINTED = new Set(['build', 'code', 'overview', 'scene'])

// The story's chips: a version's publish only.
const chipsOf = (s, dt) => {
  if (!s || !s.n) return []
  const out = []
  const from = s.lost ? s.lost.n : s.made && s.made.data && s.made.data.from ? Number(s.made.data.from) : null
  if (s.current && (s.uploaded || s.made || s.lost || s.created)) out.push({ key: 'current', from, to: s.n })
  const ctx = s.ctx || {}
  if (s.failed) out.push({ key: 'upload', text: 'upload failed', tone: 'bad' })
  else if (s.canceled) out.push({ key: 'upload', text: 'upload canceled', tone: 'warn' })
  else if (s.uploaded) out.push({ key: 'upload', text: 'upload complete', size: sizeText(ctx.size), tone: 'ok' })
  else if (s.created) out.push({ key: 'upload', text: 'upload pending', tone: 'warn' })
  // only a finished upload of a version the read found, with none saved
  if (s.uploaded && !s.failed && !s.canceled && ctx.versionID && !ctx.checksum && dt && FINGERPRINTED.has(dt.kind)) {
    out.push({ key: 'fingerprint', text: 'no file fingerprint', tone: 'warn', title: 'This upload saved no SHA-256 fingerprint of its file' })
  }
  return out.map(c => (c.size === '' ? { key: c.key, text: c.text, tone: c.tone } : c))
}

// A story (its entries, newest first; one entry is a story of one): its
// headline, chips and lines. headline null: not about content, or a view
// whose record is not the course or folder itself (the entry names it).
// now: the reader's day, for a view's count ("2 times today").
export const storyView = (entries, now = new Date()) => {
  const list = (entries || []).filter(Boolean)
  const ctx = contextOf(list)
  const lines = inOrder(list).map(e => ({ entry: e, text: lineOf(e, now) }))
  if (!ctx || !ctx.course) return { headline: null, chips: [], lines, context: ctx }
  // a view is never a change (it never shares a story: no request)
  if (list.every(isView)) return { headline: isNode(list[0]) ? viewHeadline(list[0], now) : null, chips: [], lines, context: ctx }
  const node = list.find(isNode)
  const dt = dataTypeView((list.find(e => e.context && e.context.dataType) || {}).context?.dataType)
  const aboutVersions = list.some(e => isVersion(e) || factsOf(e).madeCurrent)
  let headline = null
  let chips = []
  // A course added, deleted or restored with its files is the course's story.
  const nf = node && factsOf(node)
  if (node && (nf.created || nf.removed || nf.recycled || nf.restored || !aboutVersions)) {
    headline = nodeHeadline(node, node.context)
  } else if (aboutVersions && dt) {
    const s = versionStory(list, ctx)
    const at = s.ctx || ctx
    headline = versionHeadline(s, dt, at, fileOf(list, at, dt, s.versionID))
    chips = s.made && !s.main ? chipsOf({ ...s, current: true }, dt) : chipsOf(s, dt)
  } else {
    const asset = list.find(isAsset)
    const at = (asset && asset.context) || ctx
    if (asset && dt) headline = assetHeadline(asset, dt, at, fileOf(list, at, dt))
  }
  return { headline, chips, lines, context: ctx }
}

// The field rows a placed entry still shows under its headline: none for
// a creation, deletion or restore (the headline is the whole story; Details
// lists every value), and none of the upload and current flags a version's
// headline and chips already tell; anything else (a rename's from → to, a
// move's folders, a note) stays.
const TOLD = new Set(['status', 'is_uploaded', 'is_current', 'version_count', 'deleted_at', 'upload_identifier', 'content_length', 'content_sha256', 'status_reason', 'status_message'])
export const changesBesideHeadline = (entry, changes) => {
  const f = factsOf(entry)
  if (f.created || f.removed || f.recycled || f.restored) return []
  return (changes || []).filter(c => !TOLD.has(c.field))
}
